import { type QuestionType } from '@core/blocks/edu-flow/components/Page/types';
import { type MonarchEditableAsset, type AssetQuestionOption } from '@core/context/types';

const personalizationQuestionLevelOfEducation = (
  question: QuestionType,
  {
    questionHeading,
    questionSubHeading,
    questionOptions,
    questionStepId,
    questionStepName,
    conditionalContent,
    sidePanelContentPrefix,
    sidePanelContentHeading,
    sidePanelContentValue,
  }: MonarchEditableAsset
): QuestionType => {
  // Start with question bank values
  const result = { ...question };

  // Override with Monarch values if provided
  result.heading = questionHeading || question.heading;
  result.postSubheading = questionSubHeading || question.postSubheading;
  result.stepId = questionStepId || question.stepId;
  result.stepName = questionStepName || question.stepName;

  // Handle education level options
  if (result.fields) {
    const eduLevelField = result.fields.find((f) => f.name === 'educationLevel');
    if (eduLevelField?.options && questionOptions?.length) {
      // Only update options that exactly match values from question bank
      const updatedOptions = eduLevelField.options.map((qBankOpt: AssetQuestionOption) => {
        const monarchOpt = questionOptions.find((opt) => String(opt.value) === String(qBankOpt.value));
        return monarchOpt ? { ...qBankOpt, label: monarchOpt.label } : qBankOpt;
      });

      // Assign new array to options
      eduLevelField.options = updatedOptions;
    }
  }

  // Handle graduation year field
  if (result.fields) {
    const gradYrField = result.fields.find((f) => f.name === 'graduationYearConditional');
    if (gradYrField?.conditional?.content) {
      const gradYrContent = conditionalContent?.find((c) => c.name === 'graduationYearConditional');
      if (gradYrContent && gradYrField.conditional.content) {
        // Override heading from Monarch
        if (gradYrContent.heading) {
          gradYrField.conditional.content.heading = gradYrContent.heading;
        }

        // Override min/max from Monarch
        const yearField = gradYrField.conditional.content.fields?.[0];
        if (yearField) {
          if (gradYrContent.min) yearField.min = gradYrContent.min;
          if (gradYrContent.max) yearField.max = gradYrContent.max;
        }
      }
    }
  }

  // set the additional content
  if (result.additionalContent) {
    const statCtaField = result.additionalContent.find((field) => field.name === 'statCta');
    if (statCtaField) {
      statCtaField.prefix = sidePanelContentPrefix;
      statCtaField.heading = sidePanelContentHeading;
      statCtaField.content = sidePanelContentValue;
    }
  }

  return result as QuestionType;
};

export default personalizationQuestionLevelOfEducation;
